import { BroadcastAudience, BroadcastStatus } from '../../../__generated__/graphql';
import type { BadgeTone } from '../../components/ui';

/** The scheduler gives up after this many failed sends (backend BROADCAST_MAX_SEND_ATTEMPTS). */
export const MAX_SEND_ATTEMPTS = 3;

export const TITLE_MAX = 255;
/** Push notifications show roughly this much of the summary before cutting it off. */
export const SUMMARY_SOFT_MAX = 140;
/** The earliest a broadcast can be scheduled: the job runs every minute. */
export const MIN_SCHEDULE_LEAD_MS = 2 * 60 * 1000;

export const AUDIENCE_OPTIONS: { value: BroadcastAudience; label: string; description: string }[] =
  [
    {
      value: BroadcastAudience.AllUsers,
      label: 'Everyone',
      description: 'Hosts, guests, applicants and anyone using this brand’s app or site',
    },
    {
      value: BroadcastAudience.HostsOnly,
      label: 'Hosts',
      description: 'Hosts approved on this brand',
    },
    {
      value: BroadcastAudience.GuestsOnly,
      label: 'Guests',
      description: 'Anyone who has booked a trip on this brand',
    },
  ];

export function audienceLabel(audience: BroadcastAudience): string {
  return AUDIENCE_OPTIONS.find(option => option.value === audience)?.label ?? audience;
}

/** The fields of a broadcast this page reads. */
export interface AdminBroadcast {
  id: string;
  title: string;
  summary: string;
  content: string;
  audience: BroadcastAudience;
  status: BroadcastStatus;
  scheduledAt?: string | null;
  sentAt?: string | null;
  createdAt: string;
  sendAttempts: number;
  lastSendError?: string | null;
  isSending: boolean;
  recipientCount: number;
}

export interface BroadcastStatusView {
  label: string;
  tone: BadgeTone;
  /** One line under the title: when it went or goes out, and to how many. */
  detail: string;
  /** Shown as an alert on the row when set. */
  problem?: string;
}

export function formatWhen(value: string | Date | null | undefined): string {
  if (!value) return '';
  return new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function people(count: number): string {
  return `${count.toLocaleString()} ${count === 1 ? 'person' : 'people'}`;
}

/** What the row says about a broadcast's state. */
export function broadcastStatusView(
  b: AdminBroadcast,
  now: number = Date.now(),
): BroadcastStatusView {
  if (b.status === BroadcastStatus.Sent) {
    return {
      label: 'Sent',
      tone: 'success',
      detail: `Sent ${formatWhen(b.sentAt)} to ${people(b.recipientCount)}`,
    };
  }
  if (b.isSending) {
    return {
      label: 'Sending',
      tone: 'info',
      detail: b.recipientCount ? `Delivered to ${people(b.recipientCount)} so far` : 'Starting',
    };
  }
  const partly =
    b.recipientCount > 0 ? ` It reached ${people(b.recipientCount)} before failing.` : '';
  if (b.status === BroadcastStatus.Scheduled) {
    if (b.sendAttempts >= MAX_SEND_ATTEMPTS) {
      return {
        label: 'Failed',
        tone: 'error',
        detail: `Was due ${formatWhen(b.scheduledAt)}`,
        problem: `Stopped after ${b.sendAttempts} failed attempts: ${b.lastSendError ?? 'unknown error'}.${partly} Send it now or reschedule it to try again.`,
      };
    }
    if (b.sendAttempts > 0) {
      return {
        label: 'Retrying',
        tone: 'warning',
        detail: `Was due ${formatWhen(b.scheduledAt)}`,
        problem: `Attempt ${b.sendAttempts} of ${MAX_SEND_ATTEMPTS} failed: ${b.lastSendError ?? 'unknown error'}.${partly} It is retried within a minute.`,
      };
    }
    const due = b.scheduledAt ? new Date(b.scheduledAt).getTime() <= now : false;
    return {
      label: 'Scheduled',
      tone: 'brand',
      detail: due ? 'Going out now' : `Goes out ${formatWhen(b.scheduledAt)}`,
    };
  }
  if (b.sendAttempts > 0) {
    return {
      label: 'Not finished',
      tone: 'warning',
      detail: `Created ${formatWhen(b.createdAt)}`,
      problem: `The last send failed: ${b.lastSendError ?? 'unknown error'}.${partly} Send it again to reach everyone else; nobody gets it twice.`,
    };
  }
  return { label: 'Draft', tone: 'neutral', detail: `Created ${formatWhen(b.createdAt)}` };
}

export interface BroadcastActions {
  edit: boolean;
  send: boolean;
  unschedule: boolean;
  remove: boolean;
}

/** Mirrors the backend rules, so the page never offers a call the API refuses. */
export function broadcastActions(b: AdminBroadcast): BroadcastActions {
  const open = b.status !== BroadcastStatus.Sent && !b.isSending;
  return {
    edit: open,
    send: open,
    unschedule: open && b.status === BroadcastStatus.Scheduled,
    // A partly sent broadcast stays: its notifications open it.
    remove: open && b.sendAttempts === 0 && b.recipientCount === 0,
  };
}

export interface BroadcastDraft {
  title: string;
  summary: string;
  content: string;
  audience: BroadcastAudience;
}

export type DraftErrors = Partial<Record<keyof BroadcastDraft | 'scheduledAt', string>>;

export function validateDraft(draft: BroadcastDraft): DraftErrors {
  const errors: DraftErrors = {};
  if (!draft.title.trim()) errors.title = 'Add a title';
  else if (draft.title.length > TITLE_MAX)
    errors.title = `Keep the title under ${TITLE_MAX} characters`;
  if (!draft.summary.trim()) errors.summary = 'Add a summary; it is the push notification text';
  if (!draft.content.trim()) errors.content = 'Add the message';
  return errors;
}

/** `datetime-local` value (the admin's own time zone) for a date. */
export function toDateTimeLocalValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

/** Parses a `datetime-local` value as the admin's local time. */
export function fromDateTimeLocalValue(value: string): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function validateScheduleTime(value: string, now: number = Date.now()): string | null {
  const date = fromDateTimeLocalValue(value);
  if (!date) return 'Pick a date and time';
  if (date.getTime() < now + MIN_SCHEDULE_LEAD_MS) return 'Pick a time at least 2 minutes from now';
  return null;
}

/** Default schedule: the next full hour at least an hour away. */
export function defaultScheduleValue(now: Date = new Date()): string {
  const next = new Date(now.getTime() + 60 * 60 * 1000);
  next.setMinutes(0, 0, 0);
  if (next.getTime() - now.getTime() < 60 * 60 * 1000) next.setHours(next.getHours() + 1);
  return toDateTimeLocalValue(next);
}
